import {createAsyncThunk, createAction} from '@reduxjs/toolkit';
import {
    getUserData,
    loginUserApi,
    refreshAccessTokenApi,
    logoutUser,
    registerUserApi,
    resetPasswordApi,
    confirmUsernameApi,
    requestResetLinkApi,
    changePasswordApi,
    loginUserFromTokenApi,
    updateUserApi
} from '../../services/api/user.api';
import RootState from '../states/root.state';
import {jwtDecode} from 'jwt-decode';
import {refreshWithLock} from '../utils/tokenSync';

export const fetchUserData = createAsyncThunk<any, { impersonate_role?: string } | void, { rejectValue: string; state: RootState}>(
    'user/fetchData',
    async (params, {rejectWithValue, getState, dispatch}) => {
        const {user} = getState();
        const token = user.accessToken;

        if (!token) {
            return rejectWithValue('No access token found');
        }

        try {
            const impersonate_role = params?.impersonate_role;
            return await getUserData(token, impersonate_role);
        } catch (error: any) {
            if (error.response) {
                return rejectWithValue(error.response.data.data.message??error.response.data.message??"An error occurred while fetching user data.");
            } else {
                return rejectWithValue('An error occurred while fetching user data.');
            }
        }
    }
);

export interface LoginResponse {
    accessToken: string;
    expirationTime: number;
    user: Object;
}

export interface registerResponse {
    accessToken: string;
    expirationTime: number;
    user: Object;
}

export interface ResetPasswordResponse {
    status: string;
    message?: string;
}

export interface ForgotPasswordResponse {
    status: string;
    message?: string;
    /** Set when the reset link was sent for one specific account. */
    username?: string;
    /** True when the email is shared by several accounts and a username list was emailed instead. */
    multiple_accounts?: boolean;
    account_count?: number;
}

export interface ConfirmUsernameResponse {
    status: string;
}

export interface RefreshTokenResponse {
    accessToken: string;
    expirationTime: number;
}

export interface changePasswordResponse {
    status: string;
}

export const loginUser = createAsyncThunk<
    LoginResponse,
    { Email: string; Password: string },
    { rejectValue: string }
>(
    'user/login',
    async (credentials, {rejectWithValue}) => {
        try {
            const response = await loginUserApi(credentials);
            const decodedToken = jwtDecode(response.access_token);

            const expirationTime = Math.floor((decodedToken.exp ?? 0) * 1000); // Convert expiration time to integer milliseconds

            return {
                accessToken: response.access_token,
                expirationTime: expirationTime,
                user: response.user
            };
        } catch (error: any) {
            if (error.response) {
                if(error.response.data.errors || error.response.data.data.errors){
                    return rejectWithValue(JSON.stringify(error.response.data.errors)??JSON.stringify(error.response.data.data.errors)??"An error occurred while registering.");
                } else {
                    return rejectWithValue(error.response.data.data??error.response.data??"An error occurred while logging in.");
                }            
            } else {
                return rejectWithValue('An error occurred during login.');
            }
        }
    }
);

// Gives a concurrent refresh time to land its new cookie before retrying.
const REFRESH_RETRY_DELAY_MS = 2000;

const isRejectedRefresh = (error: any): boolean => {
    const status = error?.response?.status;
    return status === 400 || status === 401 || status === 403;
};

export const refreshAccessToken = createAsyncThunk<
    RefreshTokenResponse,
    void,
    { rejectValue: string, state: RootState }
>(
    'user/refreshAccessToken',
    async (_, {rejectWithValue, dispatch, getState}) => {
        try {
            return await refreshWithLock(getState().user.accessToken, async () => {
                const callRefresh = async () => {
                    const response = await refreshAccessTokenApi();
                    const decodedToken = jwtDecode(response.access_token);
                    const expirationTime = Math.floor((decodedToken.exp ?? 0) * 1000);
                    return {accessToken: response.access_token, expirationTime: expirationTime};
                };
                try {
                    return await callRefresh();
                } catch (error: any) {
                    if (!isRejectedRefresh(error)) throw error;
                    // A refresh this tab didn't coordinate with (another build of the portal,
                    // or a tab that gave up waiting for the lock) may have rotated the cookie
                    // at the same moment. The browser then already holds the new one, so try
                    // once more before treating the session as over.
                    console.warn('[refreshAccessToken] Refresh token rejected, retrying once in case it was just rotated');
                    await new Promise(resolve => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
                    return await callRefresh();
                }
            });
        } catch (error: any) {
            // Only a rejected refresh token means the session is over. A server error or
            // rate limit shouldn't log the user out.
            if (isRejectedRefresh(error)) {
                // Clear user state if the refresh token is also expired/invalid to redirect the user to the login page to obtain a new one.
                dispatch(clearUserState());
                return rejectWithValue(error.response.data?.data?.message??error.response.data?.message??"An error occurred while refreshing the access token.");
            } else {
                return rejectWithValue('An error occurred while refreshing the access token.');
            }
        }
    }
);

export const logout = createAsyncThunk<void, void, { rejectValue: string, state: RootState }>(
    'user/logout',
    async (_, {rejectWithValue, dispatch, getState}) => {
        try {
            const {user} = getState();
            const token = user.accessToken;
            await logoutUser(token);
            sessionStorage.removeItem("impersonate");
            dispatch(clearUserState());
        } catch (error: any) {
            if (error.response) {
                return rejectWithValue(error.response.data.data.message??error.response.data.message??"An error occurred while logging out.");
            } else {
                return rejectWithValue('An error occurred during logout.');
            }
        }
    }
);

export const clearUserState = createAction('user/clearState');

/* Another tab logged in or refreshed; use its token. */
export const syncAccessToken = createAction<RefreshTokenResponse>('user/syncAccessToken');


/* Register user */
export const registerUser = createAsyncThunk<
    registerResponse,
    { FirstName: string; LastName: string, UserName: string; Email: string; Password: string; PasswordConfirmation: string; DealerID: string },
    { rejectValue: string }
>(
    'user/register',
    async (credentials, {rejectWithValue}) => {
        try {
            const response = await registerUserApi(credentials);
            const decodedToken = jwtDecode(response.access_token);

            const expirationTime = (decodedToken.exp ?? 0) * 1000; // Convert expiration time to milliseconds

            return {
                accessToken: response.access_token,
                expirationTime: expirationTime,
                user: response.user
            };
        } catch (error: any) {
            if (error.response) {
                if(error.response.data.errors || error.response.data.data.errors){
                    return rejectWithValue(JSON.stringify(error.response.data.errors)??JSON.stringify(error.response.data.data.errors)??"An error occurred while registering.");
                } else {
                    return rejectWithValue(error.response.data.data??error.response.data??"An error occurred while logging in.");
                }
            } else {
                return rejectWithValue('An error occurred during registration.');
            }
        }
    }
);


export const resetPassword = createAsyncThunk<
    ResetPasswordResponse,
    { email: string; password: string, password_confirmation: string; token: string;},
    { rejectValue: string }
>(
    'password/reset',
    async (credentials, {rejectWithValue}) => {
        try {
            const response = await resetPasswordApi(credentials);

            return {
                status: response.status,
                message: response.data?.message || 'Password reset successfully.'
            };
        } catch (error: any) {
            if (error.response) {
                return rejectWithValue(JSON.stringify(error.response.data.errors)??error.response.data.message??"An error occurred while resetting password.");
            } else {
                return rejectWithValue('An error occurred during resetting password.');
            }
        }
    }
);



export const confirmUsername = createAsyncThunk<
    ConfirmUsernameResponse,
    { username: string;},
    { rejectValue: string }
>(
    'user/confirm-username',
    async (credentials, {rejectWithValue}) => {
        try {
            const response = await confirmUsernameApi(credentials);
            return {
                status: response.status
            };
        } catch (error: any) {
            if (error.response) {
                return rejectWithValue(error.response.data.data || error.response.data.message || 'An error occurred while confirming username.');
            } else {
                return rejectWithValue('An error occurred during username confirmation.');
            }
        }
    }
);

export const requestResetLink = createAsyncThunk<
    ForgotPasswordResponse,
    { email: string; username?: string;},
    { rejectValue: string }
>(
    'password/request-reset-link',
    async (credentials, {rejectWithValue}) => {
        try {
            const response = await requestResetLinkApi(credentials);
            const data = response?.data ?? {};
            return {
                status: response.status,
                message: data.message,
                username: data.username,
                multiple_accounts: data.multiple_accounts === true,
                account_count: data.account_count,
            };
        } catch (error: any) {
            if (error.response) {
                return rejectWithValue(error.response.data.data || error.response.data.message || 'An error occurred while requesting reset link.');
            } else {
                return rejectWithValue('An error occurred during requesting reset link.');
            }
        }
    }
);

/* Edit user profile */
export const changePassword = createAsyncThunk<
    changePasswordResponse,
    {old_password: string, password: string, password_confirmation: string},
    { rejectValue: string; state: RootState}
>(
    'user/update-password',
    async (credentials, {rejectWithValue, getState}) => {
        try {
            const {user} = getState();
            const token = user.accessToken;
            const response = await changePasswordApi(token,credentials);

            return response.data;

        } catch (error: any) {
            if (error.response) {
                return rejectWithValue(error.response.data.data.message??error.response.data.message??"An error occurred while changing password.");
            } else {
                return rejectWithValue('An error occurred during password change.');
            }
        }
    }
);

  export const loginUserFromToken = createAsyncThunk<
    LoginResponse,
    string|null
>(
    'user/token',
    async (jwt, {rejectWithValue}) => {
        try {
            const response = await loginUserFromTokenApi(jwt);
            const decodedToken = jwtDecode(response.access_token);

            const expirationTime = (decodedToken.exp ?? 0) * 1000; // Convert expiration time to milliseconds

            return {
                accessToken: response.access_token,
                expirationTime: expirationTime,
                user: response.user
            };
        } catch (error: any) {
            if (error.response) {
                if (error.response.data.data.message!==undefined) {
                    return rejectWithValue(error.response.data.data.message??error.response.data.message??"An error occurred while logging in.");
                } else {
                    return rejectWithValue(error.response.data.data.message);
                }
                
            } else {
                return rejectWithValue('An error occurred during login.');
            }
        }
    }
);

export const updateUser = createAsyncThunk<any, 
    {
    uuid:string, 
    name?:string, 
    username?:string, 
    email?:string,
    dealership_id?:string,
    phone?:string|null
}, { rejectValue: string; state: RootState}>(
    'user/update',
    async (updateInfo, {rejectWithValue, getState}) => {
        try {
            const {user} = getState();
            const token = user.accessToken;
            const currentUserId = user.data?.id || user.data?.uuid;
            const isSelfUpdate = currentUserId && (currentUserId === updateInfo.uuid || String(currentUserId) === String(updateInfo.uuid));
            
            const response = await updateUserApi(token,updateInfo);
            return {
                user: response.data,
                isSelfUpdate: !!isSelfUpdate
            };

        } catch (error: any) {
            if (error.response) {
                if (error.response.data.errors.email || error.response.data.errors.username || error.response.data.errors.phone) {
                    return rejectWithValue(error.response.data.errors);
                } else {
                    return rejectWithValue(error.response.data.data.message??error.response.data.message??"An error occurred while editing.");
                }
            } else {
                return rejectWithValue('An error occurred during editing.');
            }
        }
    }
);
